import { useState } from 'react'

import { F0Text } from '@factorialco/f0-react'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay, Tabs } from '@factorialco/f0-react/dist/experimental'
import {
  ContractsEmployeesJobCatalogTreeFragment,
  ResourceFieldDataFragment,
  TaskDetailAffecteeReferenceContractFragment,
  TaskDetailContractContextlessPoliciesFragment,
  TaskDetailCustomFieldsContextlessPoliciesFragment,
  TaskDetailJobCatalogContextlessPoliciesFragment,
  ValueDataFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { WorkflowOwner } from 'modules/tasks/hooks/useMaterializedTaskItem'

import Attachments from 'modules/tasks/components/TaskDetails/Attachments'
import { Comments } from 'modules/tasks/components/TaskDetails/Comments'
import DataCollection from 'modules/tasks/components/TaskDetails/DataCollection'
import DocumentSignatures from 'modules/tasks/components/TaskDetails/DocumentSignatures'
import TimeTracked from 'modules/tasks/components/TaskDetails/TimeTracked'
import UploadDocumentsTypeAttachments from 'modules/tasks/components/TaskDetails/UploadDocumentsTypeAttachments'
import { TaskDetail } from 'modules/tasks/components/TaskElement/useTaskDetail'
import { TaskFile } from 'modules/tasks/hooks/useGetTaskFiles'

import './index.styles.css'

type TabId = 'comments' | 'time_tracked'

interface Props {
  loadingFiles?: boolean
  task: TaskDetail
  files?: TaskFile[]
  owner: WorkflowOwner | undefined
  canSeePersonalInfo: boolean
  canEditPersonalInfo: boolean
  canEmployeeManageAnyEmployeeFolder: boolean
  customResourceFieldsData: ResourceFieldDataFragment[]
  customValuesData: ValueDataFragment[]
  hasDevTimeTrackingWorkableDaysDefinition?: boolean
  hasTimeTrackingAnnualisationDailyValue?: boolean
  referenceContract?: TaskDetailAffecteeReferenceContractFragment | null
  jobCatalogContextlessPolicies: TaskDetailJobCatalogContextlessPoliciesFragment | undefined
  hasPayrollBundle: boolean
  contractContextlessPolicies: TaskDetailContractContextlessPoliciesFragment | undefined
  customFieldsContextlessPolicies: TaskDetailCustomFieldsContextlessPoliciesFragment | undefined
  jobCatalogTreeFeatureEnabled?: boolean
  jobCatalogTrees?: ContractsEmployeesJobCatalogTreeFragment[]
  hideComments?: boolean
  hasProjectsTasksTimeTracking?: boolean
  canManageProjectTask?: boolean
}

const Content = ({
  task,
  files = [],
  loadingFiles = false,
  owner,
  canSeePersonalInfo,
  canEditPersonalInfo,
  canEmployeeManageAnyEmployeeFolder,
  customResourceFieldsData,
  customValuesData,
  hasDevTimeTrackingWorkableDaysDefinition,
  hasTimeTrackingAnnualisationDailyValue,
  referenceContract,
  jobCatalogContextlessPolicies,
  hasPayrollBundle,
  contractContextlessPolicies,
  customFieldsContextlessPolicies,
  jobCatalogTreeFeatureEnabled,
  jobCatalogTrees,
  hideComments,
  hasProjectsTasksTimeTracking,
  canManageProjectTask,
}: Props) => {
  const content = task.content

  const showTimeTrackedTab =
    hasProjectsTasksTimeTracking === true &&
    canManageProjectTask === true &&
    task.identifier === 'project' &&
    task.projectTask != null

  const [activeTab, setActiveTab] = useState<TabId>('comments')

  const superTask = () => {
    const type = 'type' in task ? task.type : null
    switch (type) {
      case 'sign_documents':
      case 'send_document_template':
        return <DocumentSignatures task={task} owner={owner} />
      case 'upload_document':
        return (
          <UploadDocumentsTypeAttachments
            task={task}
            canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
          />
        )
      case 'data_collection':
        return (
          <DataCollection
            task={task}
            canSeePersonalInfo={canSeePersonalInfo}
            canEditPersonalInfo={canEditPersonalInfo}
            canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
            referenceContract={referenceContract}
            customResourceFieldsData={customResourceFieldsData}
            customValuesData={customValuesData}
            hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
            hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
            jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
            hasPayrollBundle={hasPayrollBundle}
            contractContextlessPolicies={contractContextlessPolicies}
            customFieldsContextlessPolicies={customFieldsContextlessPolicies}
            jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
            jobCatalogTrees={jobCatalogTrees}
          />
        )
      default:
        return null
    }
  }

  const renderBottomSection = () => {
    if (hideComments) return null

    if (showTimeTrackedTab) {
      return (
        <Box gap="s16">
          <Box className="Content_fullWidthBleed">
            <Tabs
              tabs={[
                { id: 'comments', label: i18n.t('tasks.tabs.comments') },
                { id: 'time_tracked', label: i18n.t('tasks.tabs.time_tracked') },
              ]}
              activeTabId={activeTab}
              setActiveTabId={setActiveTab}
            />
          </Box>
          {activeTab === 'comments' && <Comments taskId={task.id} hideTitle />}
          {activeTab === 'time_tracked' && task.projectTask != null && (
            <Box className="Content_fullWidthBleed">
              <TimeTracked projectTaskId={task.projectTask.id} />
            </Box>
          )}
        </Box>
      )
    }

    return <Comments taskId={task.id} />
  }

  return (
    <Box gap="s40">
      <Box gap="s16" width="full" className={'Content_textWidth'}>
        <>
          <F0Text variant="label" content={i18n.t('tasks.labels.content')} />
          {content ? (
            <RichTextDisplay content={content} />
          ) : (
            <Text.Normal color="grey600">
              {i18n.t('tasks.task_details_description_empty')}
            </Text.Normal>
          )}
        </>
      </Box>
      <Attachments files={files} loadingFiles={loadingFiles} />
      {superTask()}
      {renderBottomSection()}
    </Box>
  )
}

export default Content
